/**
 * Conservative frustration detector for the Evie widget. Pure and dependency-free.
 *
 * A hit changes Evie's tone (apology first, support offered sooner), so a false positive costs
 * more than a miss. Only clear signals count: irritation words, shouting, hammered punctuation,
 * or an explicit demand for a person.
 */

export interface Sentiment {
  /** The message reads as irritated or angry. */
  frustrated: boolean;
  /** The user explicitly wants a human, a manager, or a call right now. Implies `frustrated`. */
  demandsHuman: boolean;
}

export const FRUSTRATION_PATTERNS: RegExp[] = [
  /\b(ridiculous|unacceptable|terrible|awful|useless|pathetic|incompetent|disgrace(ful)?|a joke|worst|appalling|hopeless)\b/i,
  /\b(frustrat|annoy|angry|furious|fed up|sick of|had enough|disappoint|infuriat|outrag)/i,
  /waste of (my |our )?time/i,
  /\b(again|still|yet again)\b.*\b(waiting|nothing|no (reply|answer|update|response|one))\b/i,
  /\b(damn|hell|crap|wtf|bloody|bs|bullshit|shit|fuck\w*)\b/i,
  /(!{3,}|\?{3,}|(\?!|!\?){2,})/,
];

export const DEMAND_PATTERNS: RegExp[] = [
  /\b(now|immediately|right now|asap|today|this instant)\b.*\b(human|person|someone|manager|supervisor|agent|call|phone|ring)\b/i,
  /\b(human|person|someone|manager|supervisor|agent|call|phone|ring)\b.*\b(now|immediately|right now|asap|today|this instant)\b/i,
  /\b(manager|supervisor)\b/i,
  /(let me|i want to|i need to|i demand to|get me|put me through to|connect me (to|with)) (talk|speak|chat)? ?(to |with )?(a |an )?(human|person|real person|someone|somebody|actual person)/i,
  /\b(stop|quit|enough) (sending|giving|with) (me )?(the )?(bots?|canned|automated|scripted)/i,
  /\b(not|no more) (a )?(bot|robot|chatbot|automated)/i,
];

const MIN_SHOUT_LETTERS = 8;
const SHOUT_RATIO = 0.7;

export function isShouting(text: string): boolean {
  const letters = text.replace(/[^a-zA-Z]/g, '');
  if (letters.length < MIN_SHOUT_LETTERS) return false;
  const upper = letters.replace(/[^A-Z]/g, '').length;
  return upper / letters.length >= SHOUT_RATIO;
}

export function detectSentiment(text: string | null | undefined): Sentiment {
  const raw = String(text ?? '').trim();
  if (!raw) return { frustrated: false, demandsHuman: false };
  const demandsHuman = DEMAND_PATTERNS.some((re) => re.test(raw));
  const frustrated = demandsHuman || isShouting(raw) || FRUSTRATION_PATTERNS.some((re) => re.test(raw));
  return { frustrated, demandsHuman };
}
